/**
 * Logo list auto-scroll (marquee).
 * Continuously scrolls a `.logolist--inner[data-logolist-marquee]` row to the
 * left using native scrollLeft, so the row stays a real, user-scrollable
 * element (drag with a mouse, swipe on touch, trackpad, shift+wheel) while
 * also moving on its own. The block list is rendered twice in the section
 * markup; once the first copy has scrolled fully out of view we jump back by
 * exactly that width, which is seamless because the two copies are
 * identical.
 */
(function () {
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DRAG_THRESHOLD = 6; // px of pointer movement before a click becomes a drag

  function initLogolistMarquee(section) {
    if (section.dataset.logolistMarqueeInit === 'true') return;
    var viewport = section.querySelector('.logolist--inner');
    var track = section.querySelector('.logolist--track');
    if (!viewport || !track) return;
    section.dataset.logolistMarqueeInit = 'true';

    var speed = parseFloat(section.getAttribute('data-speed')) || 60; // px per second
    var pauseOnHover = section.getAttribute('data-pause-on-hover') === 'true';

    var halfWidth = 0;
    function measure() {
      halfWidth = track.scrollWidth / 2;
    }
    measure();
    window.addEventListener('resize', measure);

    var paused = false;
    var resumeTimer = null;
    var lastTimestamp = null;

    function pause() {
      paused = true;
    }
    function resume() {
      paused = false;
      lastTimestamp = null;
    }
    function scheduleResume(delay) {
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(resume, delay || 1200);
    }

    if (pauseOnHover) {
      viewport.addEventListener('mouseenter', pause);
      viewport.addEventListener('mouseleave', function () {
        if (!isDown) resume();
      });
    }

    // Wheel / touch scrolling: pause briefly, then resume on their own.
    ['touchstart', 'wheel'].forEach(function (evt) {
      viewport.addEventListener(evt, function () {
        pause();
        scheduleResume();
      }, { passive: true });
    });

    // Let desktop mouse users drag the row left/right, same as a touch swipe,
    // WITHOUT breaking a plain click on a logo link. Dragging only "starts"
    // once the pointer has actually moved past a small threshold; a click
    // with no movement is left alone so the link still navigates normally.
    var isDown = false;
    var isDragging = false;
    var justDragged = false;
    var dragStartX = 0;
    var dragStartScroll = 0;

    viewport.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      isDown = true;
      isDragging = false;
      dragStartX = event.clientX;
      dragStartScroll = viewport.scrollLeft;
    });

    viewport.addEventListener('pointermove', function (event) {
      if (!isDown) return;
      var dx = event.clientX - dragStartX;
      if (!isDragging && Math.abs(dx) > DRAG_THRESHOLD) {
        isDragging = true;
        pause();
        viewport.classList.add('logolist--dragging');
        if (viewport.setPointerCapture) {
          try { viewport.setPointerCapture(event.pointerId); } catch (e) {}
        }
      }
      if (isDragging) {
        viewport.scrollLeft = dragStartScroll - dx;
      }
    });

    function endDrag() {
      if (!isDown) return;
      isDown = false;
      if (isDragging) {
        isDragging = false;
        justDragged = true;
        viewport.classList.remove('logolist--dragging');
        window.setTimeout(function () { justDragged = false; }, 50);
      }
      scheduleResume(800);
    }
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('pointerleave', function () {
      if (isDown) endDrag();
    });

    // If the pointer actually dragged (not just clicked), don't let that
    // gesture also trigger the logo link's navigation.
    track.addEventListener('click', function (event) {
      if (justDragged) {
        event.preventDefault();
      }
    }, true);

    if (reduceMotion || halfWidth <= 0) return;

    function normalizeLoop() {
      if (viewport.scrollLeft >= halfWidth) {
        viewport.scrollLeft -= halfWidth;
        dragStartScroll -= halfWidth;
      } else if (viewport.scrollLeft < 0) {
        viewport.scrollLeft += halfWidth;
        dragStartScroll += halfWidth;
      }
    }

    function step(timestamp) {
      if (!paused && !isDragging) {
        if (lastTimestamp !== null) {
          var elapsed = (timestamp - lastTimestamp) / 1000;
          viewport.scrollLeft += speed * elapsed;
        }
        lastTimestamp = timestamp;
      } else {
        lastTimestamp = null;
      }
      normalizeLoop();
      window.requestAnimationFrame(step);
    }

    window.requestAnimationFrame(step);
  }

  function init() {
    document.querySelectorAll('[data-logolist-marquee]').forEach(initLogolistMarquee);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when a merchant adds/edits this section in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[data-logolist-marquee]');
    if (section) initLogolistMarquee(section);
  });
})();
